import { Crown } from 'lucide-react'
import { scorePrediction } from '../scoring'
import { tierForPoints } from '../gameTier'
import { getClubAbbr } from '../clubBanners'
import { ResultTag } from './gameCard/ResultTag'
import { formatKickoffLabel } from './gameCard/kickoffLabel'
import type { Game, Prediction } from '../types'
import './GameCard.css'
import './OtherPlayerGameweekReveal.css'

export interface FixtureState {
  /** Whether this specific fixture's own kickoff has happened — the section
   * itself is gated on the gameweek deadline (see `canRevealOtherPlayerGameweek`
   * in App.tsx), but each fixture within it renders live/plain independently. */
  kickedOff: boolean
  score?: { homeScore: number; awayScore: number }
}

interface OtherPlayerCallsGridProps {
  games: Game[]
  predictions: Record<string, Prediction>
  bankerGameId: string | null
  fixtureStates: Record<string, FixtureState>
}

/** Another player's fixture cards for one gameweek, and nothing else — no label, no total, no
 * chip line. Two callers frame it differently: the profile's `GW N Calls` section, and the
 * Predict page's top-scorer modal, which carries its own header.
 *
 * Every card mounts straight into its current state (no count-up, no ignite ring, no
 * settle-bounce, no stagger), the same way the self-view renders under
 * `prefers-reduced-motion`, just applied unconditionally here rather than only under that OS
 * setting. */
export default function OtherPlayerCallsGrid({
  games,
  predictions,
  bankerGameId,
  fixtureStates,
}: OtherPlayerCallsGridProps) {
  return (
    <div className="other-reveal__games">
      {games.map((game) => {
        const prediction = predictions[game.id]
        const fixture = fixtureStates[game.id]
        const isBanker = bankerGameId === game.id

        if (!fixture?.kickedOff) {
          return (
            <div
              key={game.id}
              className="game-card other-reveal__card game-card--predicted"
            >
              {isBanker && (
                <span className="game-card__banker-badge">
                  <Crown aria-hidden="true" />
                </span>
              )}
              {/* Same top slot as the self-view card: it holds the two
                  grids to the same height, and keeps the row of abbreviations clear of
                  the Banker crown pinned to the card's top-right corner. */}
              <span className="game-card__time">{formatKickoffLabel(game.kickoff)}</span>
              <span className="game-card__row">
                <span className="game-card__team">{getClubAbbr(game.homeTeam)}</span>
                <span className="game-card__score">
                  {prediction ? `${prediction.homeScore}-${prediction.awayScore}` : '– : –'}
                </span>
                <span className="game-card__team">{getClubAbbr(game.awayTeam)}</span>
              </span>
            </div>
          )
        }

        const score = fixture.score
        const points = prediction && score ? scorePrediction(prediction, score).points : 0
        const tier = tierForPoints(points)

        return (
          <div
            key={game.id}
            className={`game-card other-reveal__card game-card--tier-${tier}`}
          >
            {isBanker && (
              <span className="game-card__banker-badge">
                <Crown aria-hidden="true" />
              </span>
            )}
            {/* A fixture nobody predicted has no verdict to report, so the tag renders
                empty rather than claiming a MISS — still occupying its line, so the
                card keeps the same geometry as its neighbours. */}
            {prediction ? (
              <ResultTag tier={tier} isAuto={prediction.isAutoPredicted ?? false} />
            ) : (
              <p className="game-card__ft-tag" />
            )}
            <span className="game-card__row">
              <span className="game-card__team">{getClubAbbr(game.homeTeam)}</span>
              <span className="game-card__score">
                {score ? `${score.homeScore}-${score.awayScore}` : '– : –'}
              </span>
              <span className="game-card__team">{getClubAbbr(game.awayTeam)}</span>
            </span>
            <span className="game-card__result-divider">
              <span className="game-card__your-pick">
                {prediction
                  ? `PLAYER PICK ${prediction.homeScore}-${prediction.awayScore}`
                  : 'No prediction'}
              </span>
              <span className="game-card__points">{points} pts</span>
            </span>
          </div>
        )
      })}
    </div>
  )
}
